/**
 * services/push.js — FCM web push registration.
 *
 * Flow:
 *   1. Wait for the inbox service worker to be ready (sw.js, which imports
 *      firebase-messaging-compat to handle onBackgroundMessage)
 *   2. Initialize Firebase JS SDK in the page
 *   3. Request notification permission
 *   4. Get FCM token (using VAPID public key from realtime-config endpoint)
 *   5. POST token to backend → stored in push_subscriptions table
 *   6. Backend webhook fans out push notifications to all registered tokens
 *
 * Foreground messages: Supabase Realtime drives the UI + onMessage toast.
 * Background messages: sw.js (single SW) handles via firebase.messaging().
 */

import { atom } from 'nanostores';

import { getJwt } from '../stores/session.js';
import { showToast } from '../stores/ui.js';
import * as api from './api.js';

const FIREBASE_CONFIG = {
  apiKey:            'AIzaSyBtSIH6qzT6-LWpKQmiDVQzVxWexWo4xzk',
  authDomain:        'odivengagepwa.firebaseapp.com',
  projectId:         'odivengagepwa',
  messagingSenderId: '958225253698',
  appId:             '1:958225253698:web:6c27e9cc839ff09988f4db',
};

let _initialized = false;
let _currentToken = null;
// Captures the message of the most recent failure path inside initPush()
// so the Settings diagnostic panel can surface it. Cleared on every
// successful registration. Without this, every bail point logs to
// console but the agent on an installed PWA has no way to see it.
let _lastError   = null;
export function _getLastPushError() { return _lastError; }

/**
 * #187 — reactive Notification.permission so the Profile CTA can update
 * itself the moment the user toggles the OS-level permission (e.g. via
 * Site Settings) without needing a tab reload.
 *
 * Permissions API supports `permissionStatus.onchange` on Chromium-
 * family browsers; Safari + older Firefox don't, so initPushPermissionWatch()
 * also re-syncs on `visibilitychange` as a portable fallback. Either way
 * the atom always reflects the current Notification.permission value.
 */
export const $pushPermission = atom(
  typeof Notification !== 'undefined' ? Notification.permission : 'default'
);

let _permWatchWired = false;
export async function initPushPermissionWatch() {
  if (_permWatchWired || typeof Notification === 'undefined') return;
  _permWatchWired = true;
  $pushPermission.set(Notification.permission);

  // Re-sync on tab focus regardless of Permissions API support.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) $pushPermission.set(Notification.permission);
  });

  // Permissions API path — gives us instant updates on Chromium/Edge.
  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: 'notifications' });
      $pushPermission.set(status.state === 'prompt' ? 'default' : status.state);
      status.onchange = () => {
        const next = status.state === 'prompt' ? 'default' : status.state;
        $pushPermission.set(next);
        // If permission just flipped to granted, fire push registration
        // automatically so the agent doesn't have to also tap "Enable".
        if (next === 'granted' && !_initialized) {
          initPush().catch(e => console.warn('[v2 push] auto-init after grant failed:', e?.message));
        }
        // Flipped to denied — drop our cached token so the next attempt
        // re-registers with whatever fresh state the user has by then.
        if (next === 'denied') {
          _initialized = false;
          _currentToken = null;
        }
      };
    }
  } catch (e) {
    console.warn('[v2 push] Permissions API query failed:', e?.message);
  }
}

/**
 * #196 — One-shot auto-prompt on first PWA install. Bridges the gap
 * between #177 (no auto-prompt at every boot — hostile UX) and the
 * natural-feeling first-install experience users expect from other
 * PWAs (they install → get asked once → decide once).
 *
 * Conditions for the auto-prompt to fire:
 *   - Running as an installed standalone PWA (not Safari tab). The OS
 *     only adds the app to Settings → Notifications when permission is
 *     granted from the standalone context, so asking from a browser
 *     tab pollutes the wrong scope.
 *   - Notification.permission === 'default' (we've never been asked on
 *     this device).
 *   - No localStorage flag — this is the FIRST install on this device.
 *     The flag is set immediately when we prompt (whether the user
 *     accepts or dismisses) so a future page load can't re-trigger.
 *
 * Caller (app.js) defers the call by ~3s after the chat list lands so
 * the user sees the app first, not a permission modal on the boot screen.
 *
 * Returns the registered FCM token on grant, null on dismiss/skip.
 */
const _FIRST_INSTALL_PROMPTED_KEY = 'inbox_v2_first_install_prompted';

export async function maybeAutoPromptOnFirstInstall() {
  if (typeof Notification === 'undefined' || typeof window === 'undefined') return null;
  if (Notification.permission !== 'default') return null;
  // Standalone gate — same heuristic as initPush.
  const isStandalone =
    navigator.standalone === true ||
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
  if (!isStandalone) return null;
  // One-shot per device install.
  try { if (localStorage.getItem(_FIRST_INSTALL_PROMPTED_KEY) === '1') return null; } catch {}
  try { localStorage.setItem(_FIRST_INSTALL_PROMPTED_KEY, '1'); } catch {}

  // Reuse the explicit-CTA path so the toast / token-register / Permissions
  // API atom updates flow the same way they do for the manual button.
  return requestPushPermission();
}

/**
 * #177 — Explicit user-initiated permission request, called from the
 * Profile "Enable notifications" button. We deliberately removed the
 * auto-prompt from initPush() because dropping a system permission
 * dialog on every fresh boot trains users to dismiss it without
 * reading. A button click is unambiguous consent.
 */
export async function requestPushPermission() {
  if (typeof Notification === 'undefined') {
    showToast('Notifications not supported on this device', 2500);
    return null;
  }
  if (Notification.permission === 'granted') {
    // Already granted but maybe we never registered (e.g. boot ran before
    // user logged in). Run initPush to catch up.
    return initPush();
  }
  if (Notification.permission === 'denied') {
    showToast('Notifications blocked — enable in browser settings', 3500);
    return null;
  }
  const result = await Notification.requestPermission();
  $pushPermission.set(result);
  if (result !== 'granted') {
    showToast('Notifications declined', 2200);
    return null;
  }
  return initPush();
}

/**
 * Initialize FCM and register the token with our backend.
 * Idempotent — safe to call multiple times.
 */
export async function initPush() {

  if (_initialized) {
    return _currentToken;
  }

  // Browser support check
  if (!('serviceWorker' in navigator) || !('Notification' in window)) {
    _lastError = 'Service workers / Notification API not supported on this browser';
    console.warn('[v2 push] ' + _lastError);
    return null;
  }

  // #185 — iOS Safari rejects push registration from non-standalone PWAs.
  // The Notification.requestPermission call would throw a permission error
  // and the user would be confused because there's no way to recover
  // without first installing the PWA to home screen. Detect both indicators
  // (legacy navigator.standalone for Safari + display-mode media query for
  // standards-compliant browsers) and bail early with an informative log
  // so the agent can be prompted to "Add to Home Screen" first.
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (isIOS) {
    const standalone =
      navigator.standalone === true ||
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
    if (!standalone) {
      _lastError = 'iOS Safari requires the PWA to be added to Home Screen';
      console.warn('[v2 push] ' + _lastError);
      return null;
    }
    // iOS web push needs 16.4+. Older iOS reports Notification but
    // getToken will fail at the FCM step. Cheaper to short-circuit here.
    const m = ua.match(/OS (\d+)_(\d+)/);
    if (m) {
      const major = parseInt(m[1], 10);
      const minor = parseInt(m[2], 10);
      if (major < 16 || (major === 16 && minor < 4)) {
        _lastError = `iOS ${major}.${minor} is below 16.4 — push not supported`;
        console.warn('[v2 push] ' + _lastError);
        return null;
      }
    }
  }

  // #177 — Permission check ONLY. We no longer auto-request from boot:
  // the explicit Profile "Enable notifications" button calls
  // requestPushPermission() which then calls initPush() once the user
  // grants. Booting straight into a system permission dialog on a fresh
  // PWA load is hostile UX — agents dismiss it and then we can't ask
  // again on Chrome without a settings reset.
  if (Notification.permission !== 'granted') {
    $pushPermission.set(Notification.permission);
    _lastError = `Notification permission is "${Notification.permission}"`;
    return null;
  }
  $pushPermission.set('granted');

  // Wait for service worker to be ready
  let swReg;
  try {
    swReg = await navigator.serviceWorker.ready;
  } catch (e) {
    _lastError = `SW ready failed: ${e.message}`;
    console.error('[v2 push] ' + _lastError);
    return null;
  }

  // Get VAPID key from backend
  let vapidKey;
  try {
    const cfg = await api.fetchRealtimeConfig();
    vapidKey = cfg?.fcmVapidKey;
    if (!vapidKey) {
      _lastError = 'No FCM VAPID key returned from /realtime-config';
      console.warn('[v2 push] ' + _lastError);
      return null;
    }
  } catch (e) {
    _lastError = `Realtime-config fetch failed: ${e.message}`;
    console.error('[v2 push] ' + _lastError);
    return null;
  }

  // Lazy-load Firebase messaging from CDN (compat builds for SW compatibility)
  let token;
  try {
    const { initializeApp } = await import('https://www.gstatic.com/firebasejs/10.12.0/firebase-app.js');
    const { getMessaging, getToken, onMessage } = await import('https://www.gstatic.com/firebasejs/10.12.0/firebase-messaging.js');

    const app = initializeApp(FIREBASE_CONFIG);
    const messaging = getMessaging(app);

    token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swReg,
    });
    if (!token) {
      _lastError = 'FCM getToken() returned null — Apple/APNS likely rejected the subscribe';
      console.warn('[v2 push] ' + _lastError);
      return null;
    }
    _currentToken = token;

    // #210 — Foreground message handler. Data-only FCM: read from
    // payload.data (server no longer sends a notification key).
    onMessage(messaging, (payload) => {
      const d     = payload?.data || {};
      const title = d.title || payload?.notification?.title || 'New message';
      const body  = d.body  || payload?.notification?.body  || '';
      showToast(`${title}: ${body}`.substring(0, 100), 4000);
    });

  } catch (e) {
    _lastError = `Firebase init failed: ${e.message}`;
    console.error('[v2 push] ' + _lastError);
    return null;
  }

  // Register the token with our backend
  try {
    const res = await fetch('/.netlify/functions/inbox-push?action=register', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getJwt()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fcm_token: token,
        device_info: {
          ua: navigator.userAgent,
          platform: navigator.platform,
          lang: navigator.language,
        },
      }),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      _lastError = `Backend register failed: ${res.status} ${txt.slice(0, 100)}`;
      console.error('[v2 push] ' + _lastError);
      return null;
    }
  } catch (e) {
    _lastError = `Backend register fetch failed: ${e.message}`;
    console.error('[v2 push] ' + _lastError);
    return null;
  }

  _initialized = true;
  _lastError   = null;
  return token;
}

/**
 * runPushDiagnostic — comprehensive iOS PWA push-state snapshot. Returns
 * a flat object the Profile screen renders inline so the user can see
 * exactly what's wrong without needing a Mac + cable to inspect Safari.
 *
 * Why this exists: iOS Settings → Notifications only lists a PWA AFTER
 * pushManager.subscribe() has succeeded inside the standalone (installed)
 * context. If anything in the chain fails — install state, permission,
 * VAPID key, SW registration, Firebase getToken, or the backend POST —
 * the PWA stays invisible in iOS Settings and the user can't toggle
 * notifications at all. This function reports the state of every step
 * so we can pinpoint exactly which one fell over.
 */
export async function runPushDiagnostic() {
  const out = {
    when: new Date().toISOString(),
    ua: navigator.userAgent,
    is_ios: false,
    ios_version: null,
    is_standalone: false,
    notification_api: typeof Notification !== 'undefined',
    sw_api: 'serviceWorker' in navigator,
    push_manager_api: 'PushManager' in window,
    permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
    sw_registered: false,
    sw_scope: null,
    sw_state: null,
    push_subscription_present: false,
    push_subscription_endpoint: null,
    vapid_key_fetched: false,
    fcm_token_present: !!_currentToken,
    fcm_token_preview: _currentToken ? _currentToken.slice(0, 20) + '…' : null,
    backend_token_count: null,
    last_error: null,
  };

  // iOS detection + version
  out.is_ios = /iPad|iPhone|iPod/.test(out.ua) && !window.MSStream;
  if (out.is_ios) {
    const m = out.ua.match(/OS (\d+)_(\d+)/);
    if (m) out.ios_version = `${m[1]}.${m[2]}`;
  }

  // Standalone (installed PWA)
  try {
    out.is_standalone =
      navigator.standalone === true ||
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
  } catch {}

  // SW registration + push subscription
  try {
    if (out.sw_api) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        out.sw_registered = true;
        out.sw_scope = reg.scope;
        out.sw_state = reg.active?.state || (reg.installing?.state || reg.waiting?.state || 'unknown');
        if (reg.pushManager) {
          const sub = await reg.pushManager.getSubscription().catch(() => null);
          if (sub) {
            out.push_subscription_present = true;
            // Just the host part of the endpoint — don't leak the secret tail.
            try { out.push_subscription_endpoint = new URL(sub.endpoint).host; } catch {}
          }
        }
      }
    }
  } catch (e) {
    out.last_error = `sw_check: ${e.message}`;
  }

  // VAPID key reachable?
  try {
    const cfg = await api.fetchRealtimeConfig();
    out.vapid_key_fetched = !!cfg?.fcmVapidKey;
  } catch (e) {
    out.last_error = `vapid_fetch: ${e.message}`;
  }

  // How many tokens does the backend have for this user?
  try {
    const res = await fetch('/.netlify/functions/inbox-push?action=count', {
      headers: { 'Authorization': `Bearer ${getJwt() || ''}` },
    });
    if (res.ok) {
      const j = await res.json().catch(() => null);
      if (j && Number.isFinite(j.count)) out.backend_token_count = j.count;
    }
  } catch (e) {
    out.last_error = `backend_count: ${e.message}`;
  }

  // Prefer the initPush() failure message when present — it pinpoints
  // the exact step that bailed (Firebase init, getToken null, backend
  // register failed, etc) which is what the user actually needs to see.
  // The diagnostic's own probes (sw_check/vapid_fetch/backend_count
  // above) only fail when the diagnostic itself errors, which is rare.
  if (_lastError) out.last_error = _lastError;

  return out;
}

/**
 * Unregister current token from backend (called on logout).
 */
export async function unregisterPush() {
  if (!_currentToken) return;
  try {
    await fetch('/.netlify/functions/inbox-push?action=unregister', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getJwt()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fcm_token: _currentToken }),
    });
  } catch (e) {
    console.warn('[v2 push] unregister failed:', e.message);
  }
  _currentToken = null;
  _initialized = false;
}
